import * as THREE from 'three';
import { createProduct } from '@/Api';
import { LoadInformation } from '@/services/Loaders';
import { exportGlb, validateFileSize } from '@/services/Exporters';
import type { Annotation, ModelViewerSettings } from '@/types/Annotation.model';
import type { IProduct, LinkShareMode } from '@/types/Product.model';
import type { ITable } from '@/types/Table';
import type { ModelViewerPresentationSettings } from '@/constants/modelViewerPresentation';
import { presentationSettingsToModelViewerSettings } from '@/constants/modelViewerPresentation';

export interface ProductSubmissionData {
  productName: string;
  productPage: string;
  articleNumber: string;
  isPublic: boolean;
  descriptionHtml?: string;
  metadata?: unknown;
  linkShareMode?: LinkShareMode;
  linkSharePassword?: string;
  updateLinkSharePassword?: boolean;
  category?: string;
  /** Present when editing an existing product (draft continuation uses `id`) */
  linkSharePasswordSet?: boolean;
}

export function metadataTableFromTags(tagsString: string): ITable {
  return {
    _id: '',
    name: '',
    columns: {},
    rows: {
      default: {
        pNetId: 'default',
        data: { Tags: tagsString },
      },
    },
  };
}

export async function resolveGlbBlob(
  modelFile: File,
  preparedGlb: Blob | File | null | undefined,
  nonGltfModel: THREE.Object3D | null
): Promise<Blob> {
  if (preparedGlb) return preparedGlb;
  if (nonGltfModel) {
    const blob = await exportGlb(nonGltfModel, 'model.glb');
    if (!blob) {
      throw new Error('MODEL_PREPARE_FAILED');
    }
    return blob;
  }
  // Conversion still running or failed: never upload the raw IFC/FBX/… as model/gltf-binary.
  if (!/\.(glb|gltf)$/i.test(modelFile.name)) {
    throw new Error('MODEL_PREPARE_FAILED');
  }
  return modelFile;
}

/**
 * Build the multipart body used by POST /api/product (one product per request).
 */
export async function buildCreateProductFormData(args: {
  product: ProductSubmissionData;
  modelFile: File;
  modelPreviewUri: string;
  nonGltfModel?: THREE.Object3D | null;
  preparedGlb?: Blob | File | null;
  initialCameraOrbit?: string;
  initialCameraTarget?: string;
  initialCameraFieldOfView?: string;
  annotations?: Annotation[];
  presentationSettings?: ModelViewerPresentationSettings;
}): Promise<FormData> {
  const productName = (args.product.productName ?? '').trim();
  if (!productName) {
    throw new Error('NAME_REQUIRED');
  }
  if (!args.modelFile) {
    throw new Error('MODEL_REQUIRED');
  }
  if (!args.modelPreviewUri) {
    throw new Error('PREVIEW_REQUIRED');
  }

  const formData = new FormData();
  formData.append('productName', productName);
  if (args.product.productPage) formData.append('productPage', args.product.productPage);
  formData.append('articleNumber', (args.product.articleNumber ?? '').trim());
  formData.append('isPublic', args.product.isPublic.toString());
  formData.append('descriptionHtml', args.product.descriptionHtml ?? '');
  formData.append('category', (args.product.category ?? '').trim());

  const linkMode: LinkShareMode = args.product.linkShareMode ?? 'open';
  formData.append('linkShareMode', String(linkMode));
  if (linkMode === 'password') {
    const pwd = (args.product.linkSharePassword ?? '').trim();
    if (pwd.length >= 4) formData.append('linkSharePassword', pwd);
  }

  if (args.product.metadata && typeof args.product.metadata === 'object') {
    formData.append('metadata', JSON.stringify(args.product.metadata));
  }

  const imageBlob = await (await fetch(args.modelPreviewUri, { credentials: 'include' })).blob();
  formData.append('productFiles', new File([imageBlob], 'preview.png', { type: 'image/png' }));

  const modelViewerSettings: ModelViewerSettings = {
    ...(args.presentationSettings
      ? presentationSettingsToModelViewerSettings(args.presentationSettings)
      : {}),
  };
  if (args.initialCameraOrbit) modelViewerSettings.initialCameraOrbit = args.initialCameraOrbit;
  if (args.initialCameraTarget) modelViewerSettings.initialCameraTarget = args.initialCameraTarget;
  if (args.initialCameraFieldOfView) {
    modelViewerSettings.initialCameraFieldOfView = args.initialCameraFieldOfView;
  }
  if (args.annotations && args.annotations.length > 0) {
    modelViewerSettings.annotations = args.annotations;
  }
  formData.append('modelViewerSettings', JSON.stringify(modelViewerSettings));

  const glbBlob = await resolveGlbBlob(args.modelFile, args.preparedGlb, args.nonGltfModel ?? null);
  if (!validateFileSize(glbBlob)) {
    throw new Error('FILE_TOO_LARGE');
  }

  formData.append(
    'productFiles',
    new File(
      [glbBlob],
      args.modelFile.name.replace(LoadInformation.getFileExtension(args.modelFile.name), '.glb'),
      { type: 'model/gltf-binary' }
    )
  );

  return formData;
}

export async function createProductFromParts(
  args: Parameters<typeof buildCreateProductFormData>[0]
): Promise<IProduct> {
  const formData = await buildCreateProductFormData(args);
  return createProduct(formData);
}
